import { Injectable } from '@angular/core';
import { ActiveCartService, CartStateSnapshotService } from '@cart/data-access';
import { DELIVERY, PICKUP, ReceiptType } from '@checkout/const';
import { CheckoutActions, CheckoutApiService } from '@checkout/data-access';
import { isCheckoutRoute } from '@checkout/utils';
import { MyShopService } from '@my-shop/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { ROUTER_REQUEST, RouterRequestAction } from '@ngrx/router-store';
import { Pos } from '@shop/const';
import { ShopListService } from '@shop/feature';
import { RoutingService } from '@spartacus/core';
import { EMPTY, Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { AdditionalOptionsFormService, PickupService, ReceiptService, UserPickupShopsService } from '../services';

@Injectable()
export class ReceiptTypeEffects {
  loadReceiptTypes$: Observable<CheckoutActions.LoadReceiptTypeSuccess | CheckoutActions.LoadReceiptTypeFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(CheckoutActions.LOAD_RECEIPT_TYPE),
        mergeMap(() =>
          combineLatest({
            cartId: this.activeCartService.getActiveCartId(),
          }).pipe(take(1)),
        ),
        filter(({ cartId }) => !!cartId),
        switchMap(({ cartId }: { cartId: string }) => {
          if (this.cartStateSnapshotService.previousCartId === cartId) return EMPTY;

          return this.checkoutApiService.loadReceiptTypes(cartId).pipe(
            map((data: ReceiptType[]) => new CheckoutActions.LoadReceiptTypeSuccess(data)),
            catchError((error) => of(new CheckoutActions.LoadReceiptTypeFail(error))),
          );
        }),
      ),
    );

  resetReceiptTypeOnCheckoutLeave$: Observable<CheckoutActions.LoadReceiptType> = createEffect(() =>
    this.actions$.pipe(
      ofType(ROUTER_REQUEST),
      map((routerRequestAction: RouterRequestAction) => routerRequestAction.payload),
      // ресет receipt types на бэкенде
      switchMap(() => {
        this.additionalOptionsFormService.reset();

        return of(new CheckoutActions.LoadReceiptType());
      }),
    ),
  );

  presetReceiptType$: Observable<CheckoutActions.SetReceiptType | CheckoutActions.SetShop> = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.LOAD_RECEIPT_TYPE_SUCCESS),
      concatLatestFrom(() =>
        this.routingService.getRouterState().pipe(
          map((state) => state.state?.semanticRoute),
          filter((semanticRoute) => isCheckoutRoute(semanticRoute)),
        ),
      ),
      switchMap(() =>
        combineLatest({
          activeReceiptTypeId: this.receiptService.activeReceiptTypeId$.pipe(),
          presetShop: this.selectShopForCheckout(),
        }).pipe(take(1)),
      ),
      switchMap(({ activeReceiptTypeId, presetShop }) => {
        if (activeReceiptTypeId) {
          if (activeReceiptTypeId === PICKUP && presetShop) {
            return of(new CheckoutActions.SetShop(presetShop.name));
          }
        } else {
          // Если в сторе нет активного receipt type, то нужно установить его
          if (!presetShop) {
            return of(new CheckoutActions.SetReceiptType(DELIVERY));
          }

          return [new CheckoutActions.SetReceiptType(PICKUP), new CheckoutActions.SetShop(presetShop.name)];
        }

        return of();
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private checkoutApiService: CheckoutApiService,
    private activeCartService: ActiveCartService,
    private pickupService: PickupService,
    private shopListService: ShopListService,
    private routingService: RoutingService,
    private receiptService: ReceiptService,
    private myShopService: MyShopService,
    private userPickupShopsService: UserPickupShopsService,
    private additionalOptionsFormService: AdditionalOptionsFormService,
    private cartStateSnapshotService: CartStateSnapshotService,
  ) {}

  /**
   * Выбор магазина при переходе в чекаут или
   * после переключения на самовывоз
   */
  private selectShopForCheckout(): Observable<Pos | null> {
    return combineLatest({
      shopsState: this.shopListService.shopsState$,
      activeShop: this.pickupService.activeShop$,
      myShop: this.myShopService.activeShop$,
      userShopList: this.userPickupShopsService.list$,
    }).pipe(
      filter(({ shopsState: { loading, success, error, value: shops } }) =>
        Boolean(!loading && ((success && shops) || error)),
      ),
      take(1),
      map(({ shopsState: { value: shops }, activeShop, myShop, userShopList }) => {
        if (!activeShop && myShop) {
          // Устанавливать мой магазин нужно всегда, даже когда нет его в доступных магазинах
          // т.к. в чекауте мы должны оформить заказ с "недоступным" моим магазином
          return myShop;
        }

        if (shops) {
          if (activeShop && shops.some((shop) => shop.name === activeShop.name)) {
            return activeShop;
          }

          if (!activeShop) {
            return userShopList?.findLast((userShop: Pos) => shops.some((shop) => shop.name === userShop.name));
          }
        }

        return null;
      }),
    );
  }
}
